import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Window } from "@progress/kendo-react-dialogs";
import { EventFilter } from '../../../../Events/Utilities/EventsApiService';
import moment from 'moment';
import AnalysisContent, { AnalysisContentRef } from './AnalysisContent';
import { enMessages } from '../../../../../messages/en-US';
import { useLocalization } from '@progress/kendo-react-intl';

interface Props {
  selectedItems: Array<{ id: string; entityType: string }>;
  asset: any;
  sharedDateFilter?: { startDate: Date, endDate: Date };
}

export interface AnalysisTileComponentRef {
  refresh: () => Promise<void>;
}

const AnalysisTileComponent = forwardRef<AnalysisTileComponentRef, Props>(({ selectedItems, asset, sharedDateFilter }, ref) => {
  const localizationService = useLocalization();
  const [open, setOpen] = useState(false);
  const [eventFilter, setEventFilter] = useState<EventFilter>(() => {
    // Use shared date filter if provided, otherwise use default
    if (sharedDateFilter) {
      return sharedDateFilter;
    }
    return {
      endDate: moment().endOf('day').toDate(), 
      startDate: moment().subtract(7, 'days').startOf('day').toDate(), 
    };
  });
  const contentRef = React.useRef<AnalysisContentRef>(null);

  // Optionally sync with shared date filter when it changes
  useEffect(() => {
    if (sharedDateFilter) {
      setEventFilter(sharedDateFilter);
    }
  }, [sharedDateFilter]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      if (contentRef.current) {
        await contentRef.current.refresh();
      }
    }
  }));

  const handleStartDateChange = (event: any) => {
    if (event.value) {
      setEventFilter(prev => ({
        ...prev,
        startDate: moment(event.value).startOf('day').toDate(),
      }));
    }
  };

  const handleEndDateChange = (event: any) => {
    if (event.value) {
      setEventFilter(prev => ({
        ...prev,
        endDate: moment(event.value).endOf('day').toDate(),
      }));
    }
  };

  const analysisTitleLabel = localizationService.toLanguageString('custom.analysis', enMessages.custom.analysis);
  const analysisAssetLabel = typeof asset?.name === 'string' ? asset.name.trim() : '';
  const analysisWindowTitle = analysisAssetLabel
    ? `${analysisAssetLabel} - ${analysisTitleLabel}`
    : analysisTitleLabel;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        <AnalysisContent
          ref={contentRef}
          key={`${selectedItems.map(item => item.id).join('-')}-AnalysisContent`}
          selectedItems={selectedItems}
          asset={asset}
          isFullscreen={false}
          onExpand={() => setOpen(true)}
          eventFilter={eventFilter}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
      </div>
      {open && (
        <Window
          key={`${selectedItems.map(item => item.id).join('-')}-AnalysisWindow-fullscreen`}
          title={analysisWindowTitle}
          onClose={() => setOpen(false)}
          style={{ 
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: "90vw", 
            height: "92vh"
          }}
          modal={true}
          className="custom-fullscreen-window"
        >
          <div style={{ height: "80vh", display: "flex", flexDirection: "column", overflowY: 'auto' }}>
            <AnalysisContent
              ref={contentRef}
              key={`${selectedItems.map(item => item.id).join('-')}-AnalysisContent-fullscreen`}
              selectedItems={selectedItems}
              asset={asset}
              isFullscreen={true}
              eventFilter={eventFilter}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
            />
          </div>
        </Window>
      )}
    </div>
  );
});

export default AnalysisTileComponent;